import { ApiException, ErrorResponse, ValidationProblemDetails } from "../store/parentApi";

export interface ParsedApiError {
  message: string;
  correlationId?: string;
}

const DEFAULT_MESSAGE = "Something went wrong. Please try again.";

/**
 * Extracts a user-facing message and, when available, the backend's correlationId from any
 * error shape thrown by the generated API client (typed 409/400 responses, the generic
 * ApiException fallback, a raw axios error, or anything else). Never throws.
 */
export function parseApiError(error: unknown, fallbackMessage: string = DEFAULT_MESSAGE): ParsedApiError {
  try {
    if (error instanceof ErrorResponse) {
      return { message: error.message || fallbackMessage, correlationId: error.correlationId || undefined };
    }

    if (error instanceof ValidationProblemDetails) {
      return {
        message: validationMessage(error) || fallbackMessage,
        correlationId: readCorrelationId(error),
      };
    }

    if (error instanceof ApiException) {
      return parseRawResponse(error.response, fallbackMessage) ?? { message: error.message || fallbackMessage };
    }

    if (isAxiosErrorLike(error)) {
      return parseRawResponse(error.response?.data, fallbackMessage) ?? { message: fallbackMessage };
    }

    return { message: fallbackMessage };
  } catch {
    return { message: fallbackMessage };
  }
}

function validationMessage(error: ValidationProblemDetails): string {
  const errors = error.errors ?? {};
  const messages = Object.values(errors).flat().filter(Boolean);
  return messages.length > 0 ? messages.join(" ") : error.title ?? "";
}

function readCorrelationId(data: unknown): string | undefined {
  if (data && typeof data === "object" && typeof (data as any)["correlationId"] === "string") {
    return (data as any)["correlationId"];
  }
  return undefined;
}

function parseRawResponse(raw: unknown, fallbackMessage: string): ParsedApiError | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;

  let data: unknown = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }

  if (data && typeof data === "object") {
    const message =
      typeof (data as any).message === "string" && (data as any).message ? (data as any).message : fallbackMessage;
    return { message, correlationId: readCorrelationId(data) };
  }

  return undefined;
}

function isAxiosErrorLike(error: unknown): error is { response?: { data?: unknown } } {
  return typeof error === "object" && error !== null && "isAxiosError" in error;
}
